import Form from "rc-field-form";
import { useCallback, useEffect } from "react";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import Button from "../../../../components/Button";
import Field from "../../../../components/Field";
import NumberInput from "../../../../components/Inputs/NumberInput";
import ResourceSelect from "../../../../components/Inputs/ResourceSelect";
import Text from "../../../../components/Inputs/Text";
import { generateErrorsConfigForForm } from "../../../../utils/helpers/errors";
import {
	getSettingsResourceSearchFilters,
	renderSettingsResourceLabel,
} from "../../../../utils/helpers/settings";
import { useCompanyDateFormat } from "../../../../utils/hooks/company";
import { EffectiveDateFields, EffectivePreview, SubmitLabel, useEffectiveSubmit } from "./effective";

function TransferForm({ close, values, error, loading, transfer, effective, schedule }) {
	const { t } = useTranslation();
	const [form] = Form.useForm();
	const dateFormat = useCompanyDateFormat() || "MM/DD/YYYY";
	const sourcePayCode = typeof values?.plan?.payCode === "object" ? values?.plan?.payCode?.id : values?.plan?.payCode;
	const { preview, setPreview, previewing, submit } = useEffectiveSubmit({ mode: "transfer", values, effective, schedule });

	const onFinish = useCallback(async ({ effectiveDate, ...data }) => {
		data.plan = data.plan.id;
		data.amount = Number(data.amount) || 0;
		if (await submit({ ...data, effectiveDate })) {
			return;
		}
		transfer(values.id, data);
	}, [transfer, values, submit]);

	useEffect(() => {
		form.resetFields();
		form.setFieldsValue({
			plan: undefined,
			amount: (values?.bucket?.available || 0) + (values?.bucket?.pending || 0),
			comment: "",
			effectiveDate: moment().toDate(),
		});
		setPreview(null);
	}, [form, values, setPreview]);

	useEffect(() => {
		const fieldErrors = generateErrorsConfigForForm(["data"], error);
		form.setFields(fieldErrors);
	}, [form, error]);

	return (
		<Form
			form={form}
			layout="vertical"
			onFinish={onFinish}
			// a confirmed preview must be the one on screen: any change asks for a new one
			onValuesChange={() => setPreview(null)}
			scrollToFirstError
		>
			<Field
				name="plan"
				label={t("transfer-to-plan")}
				rules={[
					{
						required: true,
						message: t("required-plan"),
					},
					{
						validator: (_, value) => {
							if (value && value.id === values?.plan?.id) {
								return Promise.reject(new Error(t("transfer-to-same-plan")));
							}
							return Promise.resolve();
						},
					},
				]}
			>
				<ResourceSelect
					labelPropName="description"
					resourcePath={
						// only plans on the same pay code as the plan it leaves
						sourcePayCode
							? `/timeoff?status=active&payCode=${encodeURIComponent(sourcePayCode)}&excludeId=${encodeURIComponent(values?.plan?.id || "")}`
							: "/timeoff?status=active"
					}
					notFoundContent={t("transfer-no-plan-same-pay-code")}
					renderLabel={renderSettingsResourceLabel}
					placeholder={t("plan")}
					hasSearch
					getSearchFilters={getSettingsResourceSearchFilters}
				/>
			</Field>

			<Field
				name="amount"
				label={t("transfer-amount")}
				rules={[
					{
						required: true,
						message: t("amount-is-required"),
					},
				]}
			>
				<NumberInput placeholder={t("amount")} disabled />
			</Field>

			<Field name="comment" label={t("comment")}>
				<Text placeholder={t("comment")} />
			</Field>

			<EffectiveDateFields mode="transfer" t={t} dateFormat={dateFormat} />
			{preview && <EffectivePreview preview={preview} t={t} dateFormat={dateFormat} />}

			<div className="d-flex justify-content-end">
				<Button
					className="btn-round btn-icon shadow-none border btn btn-secondary btn-sm"
					onClick={close}
					disabled={loading}
				>
					{t("cancel")}
				</Button>

				<Button
					color="primary"
					type="submit"
					loading={loading || previewing}
					disabled={!!preview?.blocked?.length}
					className="btn-round btn-icon shadow-none border px-3 btn btn-dark btn-sm"
				>
					<SubmitLabel t={t} preview={preview} />
				</Button>
			</div>
		</Form>
	);
}

export default TransferForm;
